import { environment } from '../../../environments/environment';
import { isFeatureEnabled } from './runtime-config';

/** Gates the whole back office. Named as the backend names it (PlatformController.BackOfficeFlag). */
/**
 * The runtime flag that opens the back office. camelCase because that is what the web
 * image's entrypoint emits: each FEATURES_<NAME> becomes a camelCase key under
 * "features", and isFeatureEnabled looks it up exactly. The backend's own flag is
 * snake_case (platform_backoffice) and the two are set from the same FEATURES_ variable.
 */
export const BACK_OFFICE_FLAG = 'platformBackoffice';

/**
 * The realm role the platform realm marks an operator with — the same role the
 * backend's PlatformAdmin policy requires, so the two cannot drift apart.
 */
export const PLATFORM_ADMIN_ROLE = 'platform-admin';

/** First segment of every back-office URL. */
const BACK_OFFICE_PATH = '/platform';

/** Which realm one page load authenticates against, and whether that is the platform's. */
export interface RealmChoice {
    realm: string;
    clientId: string;
    isPlatform: boolean;
}

/** True for the back office's own URLs, and for nothing that merely starts like one. */
export function isPlatformPath(pathname: string): boolean {
    return pathname === BACK_OFFICE_PATH || pathname.startsWith(`${BACK_OFFICE_PATH}/`);
}

/**
 * Whether this environment names a platform realm at all. Exported because the
 * route has to ask it too: the flag alone would match the route in an
 * environment that cannot sign anybody into it, leaving the operator on a blank
 * page instead of the path-nobody-implemented they get today.
 */
export function isPlatformRealmConfigured(): boolean {
    return Boolean(environment.platformRealm) && Boolean(environment.platformClientId);
}

/**
 * The realm for this page load, decided from the URL before the app bootstraps.
 *
 * One Keycloak instance means one realm per page load, so the two identities
 * cannot be live at once: crossing between a shop and the back office costs a
 * reload, which is right — a platform operator and a shop's staff are different
 * people. Anything but a back-office URL gets the business realm exactly as
 * before, and so does a back-office URL whose environment is not set up for one.
 */
export function selectRealm(pathname: string): RealmChoice {
    //#region The second realm, only where the URL, the flag and the environment all allow it
    if (isPlatformPath(pathname) && isFeatureEnabled(BACK_OFFICE_FLAG) && isPlatformRealmConfigured()) {
        return { realm: environment.platformRealm, clientId: environment.platformClientId, isPlatform: true };
    }
    //#endregion

    //#region Every other URL: the business realm, unchanged
    return { realm: environment.keycloakRealm, clientId: environment.keycloakClientId, isPlatform: false };
    //#endregion
}

/** The choice this page load made. Business until a bootstrap says otherwise. */
let activeChoice: RealmChoice | null = null;

/** Records the realm the app is bootstrapping with, for anything that has to ask later. */
export function activateRealm(pathname: string): RealmChoice {
    activeChoice = selectRealm(pathname);
    return activeChoice;
}

/**
 * Whether this page load authenticated against the platform realm. The guard
 * refuses the back office without it, so a business session cannot reach it
 * however its token is decorated.
 */
export function isPlatformRealmActive(): boolean {
    return activeChoice?.isPlatform === true;
}
